/**
 * Loan Car Incident API Service
 *
 * All loan car incident operations go through /loan-car-incidents:
 * - Photo upload (shared by OUT and IN flows)
 * - Image category metadata
 * - Incident history by booking UUID, job UUID, or rego plate
 * - Structured handover status for the Job Detail view
 * - IN (return) incident recording
 *
 * Previously these lived under /bookings/loan-car/... and
 * /bookings/{uuid}/loan-car-incidents. Those endpoints no longer exist —
 * all callers should use this module instead.
 */

import axiosInstance from './axiosInstance';

// ============================================
// Photo Upload
// ============================================

/**
 * Upload a single loan car handover photo.
 * Hits POST /loan-car-incidents/upload-photo?category=... (multipart/form-data).
 *
 * Used by both the OUT flow (VehicleReceivedModal / vehicle drop-off) and the
 * IN flow (LoanCarHandoverPanel / vehicle collection). The category param
 * determines the S3 key prefix.
 *
 * @param {File}     file       - the File object from the file input / camera
 * @param {string}   category   - DRIVING_LICENCE | CAR_DASHBOARD_ON_VEHICLE_START |
 *                                INSURANCE_CERTIFICATE | OTHER
 * @param {function} onProgress - optional progress callback (0–100)
 * @returns {{ s3Key: string, category: string }}
 */
export const uploadLoanCarPhotoApi = async (file, category, onProgress) => {
    const formData = new FormData();
    formData.append('photo', file);

    const response = await axiosInstance.post('/loan-car-incidents/upload-photo', formData, {
        params: { category },
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onProgress
            ? (e) => onProgress(Math.round((e.loaded * 100) / e.total))
            : undefined,
    });
    return response.data; // { s3Key, category }
};

// ============================================
// Image Category Metadata
// ============================================

/**
 * Fetch the reference list of all valid image categories.
 * Hits GET /loan-car-incidents/image-categories.
 *
 * Returns [{ category, label, description, mandatory }]
 * where mandatory mirrors the backend validation rules:
 *   DRIVING_LICENCE                → always mandatory
 *   CAR_DASHBOARD_ON_VEHICLE_START → always mandatory
 *   INSURANCE_CERTIFICATE          → conditionally mandatory (when insuranceTaken)
 *   OTHER                          → optional
 */
export const getLoanCarImageCategoriesApi = async () => {
    const response = await axiosInstance.get('/loan-car-incidents/image-categories');
    return response.data;
};

// ============================================
// Incident History Queries
// ============================================

/**
 * Fetch all incidents (OUT and IN) recorded against a booking UUID.
 * Hits GET /loan-car-incidents/booking/{bookingUuid}.
 *
 * Used by the Booking Detail view to show the full handover history.
 * Images are returned as pre-signed URLs by the backend.
 *
 * @param {string} bookingUuid
 * @returns {LoanCarIncidentResponse[]}
 */
export const getLoanCarIncidentsByBookingApi = async (bookingUuid) => {
    const response = await axiosInstance.get(`/loan-car-incidents/booking/${bookingUuid}`);
    return response.data;
};

/**
 * Fetch all incidents (OUT and IN) for the booking linked to a job.
 * Hits GET /loan-car-incidents/job/{jobUuid}.
 *
 * Used when a flat list is needed. For the Job Detail view's Loan Car tab,
 * prefer getLoanCarHandoverStatusApi which returns structured OUT/IN objects
 * and saves a second round-trip.
 *
 * Images are returned as pre-signed URLs by the backend.
 *
 * @param {string} jobUuid
 * @returns {LoanCarIncidentResponse[]}
 */
export const getLoanCarIncidentsByJobApi = async (jobUuid) => {
    const response = await axiosInstance.get(`/loan-car-incidents/job/${jobUuid}`);
    return response.data;
};

/**
 * Fetch the structured loan car handover status for a job.
 * Hits GET /loan-car-incidents/job/{jobUuid}/handover-status.
 *
 * Used by LoanCarHandoverPanel to decide what to render:
 *
 *   { hasOut: false, hasIn: false, rego: null, ... }
 *     → No loan car on this booking. Show nothing.
 *
 *   { hasOut: true, hasIn: false, rego: 'ABC123', outIncident: {...}, inIncident: null }
 *     → OUT recorded, IN not yet done.
 *       Show the OUT record in read-only mode and the IN entry form.
 *
 *   { hasOut: true, hasIn: true, rego: 'ABC123', outIncident: {...}, inIncident: {...} }
 *     → Full round-trip complete. Show both records in read-only mode.
 *
 * Both outIncident and inIncident (when present) include imagesByCategory with
 * pre-signed URLs resolved by the backend.
 *
 * @param {string} jobUuid
 * @returns {{
 *   hasOut: boolean,
 *   hasIn: boolean,
 *   rego: string | null,
 *   outIncident: LoanCarIncidentResponse | null,
 *   inIncident: LoanCarIncidentResponse | null
 * }}
 */
export const getLoanCarHandoverStatusApi = async (jobUuid) => {
    const response = await axiosInstance.get(
        `/loan-car-incidents/job/${jobUuid}/handover-status`
    );
    return response.data;
};

/**
 * Record a loan car IN (return) incident for the booking linked to a job.
 * Hits POST /loan-car-incidents/job/{jobUuid}/return.
 *
 * Photo S3 keys must first be obtained via uploadLoanCarPhotoApi, then passed
 * in the imagesByCategory array. Backend validation requires:
 *   - odometerReading (mandatory, non-negative)
 *   - at least one CAR_DASHBOARD_ON_VEHICLE_START image
 *
 * @param {string} jobUuid
 * @param {{
 *   odometerReading: number,
 *   notes?: string,
 *   imagesByCategory: Array<{
 *     category: string,
 *     images: Array<{ s3Key: string }>
 *   }>
 * }} payload
 * @returns {LoanCarIncidentResponse}
 */
export const recordLoanCarReturnApi = async (jobUuid, payload) => {
    const response = await axiosInstance.post(
        `/loan-car-incidents/job/${jobUuid}/return`,
        payload
    );
    return response.data;
};

/**
 * Fetch all incidents for a rego plate within the org.
 * Hits GET /loan-car-incidents?rego=ABC123.
 *
 * Used by the Loan Car history view.
 * Images are returned as pre-signed URLs by the backend.
 *
 * @param {string} rego - registration plate (will be upper-cased server-side)
 * @returns {LoanCarIncidentResponse[]}
 */
export const getLoanCarIncidentsByRegoApi = async (rego) => {
    const response = await axiosInstance.get('/loan-car-incidents', {
        params: { rego },
    });
    return response.data;
};

// ============================================
// Default Export
// ============================================

const loanCarIncidentApi = {
    uploadLoanCarPhotoApi,
    getLoanCarImageCategoriesApi,
    getLoanCarIncidentsByBookingApi,
    getLoanCarIncidentsByJobApi,
    getLoanCarHandoverStatusApi,
    recordLoanCarReturnApi,
    getLoanCarIncidentsByRegoApi,
};

export default loanCarIncidentApi;